import { createFileRoute, redirect } from "@tanstack/react-router";
import { Handshake } from "lucide-react";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { userData } from "@/data/navData";
import { ensureMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { firstLeafUrl, visibleNavItems } from "@/lib/nav-visibility";
import { useFeatureStore } from "@/stores/useFeatureStore";
import { useOrgSettingsStore } from "@/stores/useOrgSettingsStore";

/**
 * Where the CRM nav entry lands when CRM is enabled but the role holds no CRM
 * permission — every CRM page needs one, even for own records. The parent
 * `crm` route already requires the feature.
 */
export const Route = createFileRoute("/user/_layout/crm/no-access")({
  beforeLoad: async () => {
    const capabilities = await ensureMyPermissions();
    const crm = visibleNavItems(
      userData.mainMenuItems.filter((item) => item.featureKey === "crm"),
      {
        capabilities,
        isEnabled: useFeatureStore.getState().isEnabled,
        settings: useOrgSettingsStore.getState().settings,
      },
    )[0];
    // Someone who does have CRM pages doesn't belong here.
    const url = crm && firstLeafUrl(crm);
    if (url) throw redirect({ to: url });
  },
  component: CrmNoAccessPage,
});

function CrmNoAccessPage() {
  return (
    <SidebarInset>
      <DashboardHeader current="CRM" />
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <Handshake className="size-12 text-muted-foreground" />
        <h1 className="text-xl font-semibold">No CRM features available</h1>
        <p className="max-w-md text-muted-foreground">
          CRM is enabled for your organization, but your role doesn't include
          any CRM permissions. Ask your administrator for access.
        </p>
      </div>
    </SidebarInset>
  );
}
